<template>
	<div class="space-y-2">
		<label for="primary-hex" class="block font-semibold">Primary Color</label>
			<div class="flex gap-5">
				<div 
					class="shrink-0 rounded aspect-square w-16 h-16"
					:style="{ backgroundColor: previewColor }"
				></div>
				<div class="space-y-2">
					<input
						id="primary-hex"
						type="text"
						v-model="hexInput"
						@input="handleHexInput"
						placeholder="Enter HEX Code"
						class="p-2 bg-zinc-800 w-full placeholder-zinc-400 rounded"
					/>
					<div class="font-mono text-xs">
						<span class="text-zinc-400 mr-2">OKLCH:</span>
						<span>{{ oklchString }}</span>
					</div>
				<div v-if="error" class="text-red-500 text-sm">{{ error }}</div>
			</div>
		</div>
	</div>
</template>

<script>
/**
 * 1. uses @input to call handleHexInput on input elem change 
 * 2. handleHEx input calls convertToOklch with the current hexInput.value
 * 3. convertToOklch processes the hex, uses colorjs to create a Color object and converts to oklch
 * 4a. the oklchValue ref then gets updated to contain the returned oklch obj
 * 4b. oklchString is a computed property that auto re-calculates
 * 4c. previewColor is also a computed property that auto-updates
 */
import Color from 'colorjs.io'
import { ref, computed } from 'vue'
import { store } from '../store.js'

export default {
  name: 'ItemConverter',
  setup() {
	const hexInput = ref('#00b6ff')
	const oklchValue = ref(null)
	const error = ref('')

	// Convert hex to oklch
	const convertToOklch = (hexValue) => {
		try {
			error.value = ''
			
			// handle '#' prefix
			let cleanHex = hexValue.trim()
			if (!cleanHex.startsWith('#')) {
				cleanHex = '#' + cleanHex
			}

			// Create color object and convert to oklch
			const color = new Color(cleanHex)
			const oklch = color.to('oklch')
			
			return {
				l: Math.round(oklch.l * 10000) / 10000, // Lightness
				c: Math.round(oklch.c * 10000) / 10000, // Chroma
				h: oklch.h ? Math.round(oklch.h * 10000) / 10000 : 0, // Hue
				alpha: oklch.alpha || 1
			}
		} catch (err) {
			error.value = 'Invalid color format'
			return null
		}
	}

	// Handle hex input change
	const handleHexInput = () => {
		const converted = convertToOklch(hexInput.value)
		oklchValue.value = converted

		if(converted) {
			store.setBaseColor(converted)
		}
	}

	// Computed property for the string
	const oklchString = computed(() => {
		if (!oklchValue.value) {
			return
		}
		
		const { l, c, h, alpha } = oklchValue.value

		if (alpha < 1) {
			return `${l} ${c} ${h} / ${alpha}`
		}
		return `${l} ${c} ${h}`
	})

	// Computed property for the preview
	const previewColor = computed(() => {
		try {
			let cleanHex = hexInput.value.trim()
			if (!cleanHex.startsWith('#')) {
				cleanHex = '#' + cleanHex
			}

			// Create color object and convert to oklch
			const color = new Color(cleanHex)
			const oklch = color.to('oklch')

			// Return CSS-compatible oklch string
			const l = oklch.l || 0
			const c = oklch.c || 0
			const h = oklch.h || 0
			const alpha = oklch.alpha || 1

			if (alpha < 1) {
				return `oklch(${l} ${c} ${h} / ${alpha})`
			}
				return `oklch(${l} ${c} ${h})`
		} catch {
			return ''
		}
	})

	// Initial call
	handleHexInput()

	return {
		hexInput,
		oklchValue,
		error,
		previewColor,
		oklchString,
		handleHexInput
	}
  },
}
</script>

<style scoped >
input:focus {
  outline: none;
}
</style>